import {
  type BackupSchedule,
  BackupPolicyType,
  ScheduleMode,
  type BackupPolicyConfig,
} from '@repo/api-gw-sdk';

import { getResourcesScopeType } from '@/components/resourcesSelection/resourceSelectionDynamicViewer';
import { Wizard } from '@/components/wizard/Wizard';
import {
  createAllIncludedCondition,
  createAllExcludedCondition,
} from '@/types/advanceFilter';

import {
  EditBackupPolicyFlow,
  type BackupPolicyEditState,
} from './BackupPolicyCreationFlow';
import { isContinuousPolicyType } from './backupPoliciesTypes';
import {
  defaultBackupPolicy,
  getDefaultBigQueryPitrSchedules,
  getDefaultStandardSchedules,
} from './defaultBackupPolicy';

interface BackupPolicyPanelProps {
  entity?: BackupPolicyConfig;
  onSave: (policy: BackupPolicyConfig) => Promise<void>;
  onAbort: () => void;
}

export function BackupPolicyPanel(props: BackupPolicyPanelProps) {
  const skipFirstStep = !!props.entity;
  const creatingNew = !props.entity;
  const backupPolicyConfig = props.entity || { ...defaultBackupPolicy };
  const schedules: BackupSchedule[] =
    backupPolicyConfig.policyType ===
    BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_PITR
      ? [
          {
            ...getDefaultStandardSchedules()[0],
            backupRetention:
              backupPolicyConfig.awsNativePitrConfig?.retentionDays || 0,
          },
        ]
      : backupPolicyConfig.policyType ===
          BackupPolicyType.BACKUP_POLICY_TYPE_BIGQUERY_NATIVE_PITR
        ? getDefaultBigQueryPitrSchedules()
        : backupPolicyConfig.schedules;

  const save = async (state: BackupPolicyEditState) => {
    const condition =
      state.resourcesScopeType === 'allIncluded'
        ? createAllIncludedCondition()
        : state.resourcesScopeType === 'allExcluded'
          ? createAllExcludedCondition()
          : state.condition;

    if (!condition) {
      return false;
    }

    const schedules = isContinuousPolicyType(state.policyType)
      ? []
      : state.schedules?.map((schedule) => ({
          ...schedule,
          useUtc:
            state.mode === ScheduleMode.SCHEDULE_MODE_ADVANCED
              ? schedule.useUtc
              : false,
        })) || [];

    await props.onSave({
      ...backupPolicyConfig,
      name: state.name,
      condition: condition,
      schedules,
      include: state.include ?? [],
      exclude: state.exclude ?? [],
      scheduleMode: state.mode,
      policyType: state.policyType,
      resourceTypes: state.resourceTypes,
      awsNativePitrConfig:
        state.policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_PITR
          ? {
              retentionDays: state.schedules?.[0]?.backupRetention,
            }
          : undefined,
      bigQueryNativePitrConfig:
        state.policyType ===
        BackupPolicyType.BACKUP_POLICY_TYPE_BIGQUERY_NATIVE_PITR
          ? {
              retentionDays: state.schedules?.[0]?.backupRetention,
            }
          : undefined,
    });

    return true;
  };

  return (
    <Wizard<BackupPolicyEditState>
      onAbort={props.onAbort}
      hideBackArrow={true}
      onFinish={save}
      initialStep={{ stepIndex: skipFirstStep ? 1 : 0 }}
      flow={[EditBackupPolicyFlow]}
      initialState={{
        id: backupPolicyConfig.id,
        name: backupPolicyConfig.name,
        focusOnName: skipFirstStep && !backupPolicyConfig.id,
        condition: backupPolicyConfig.condition,
        schedules,
        include: backupPolicyConfig.include || [],
        exclude: backupPolicyConfig.exclude || [],
        resourcesScopeType: getResourcesScopeType(
          backupPolicyConfig.condition,
          'useConditions'
        ),
        mode:
          !backupPolicyConfig.scheduleMode ||
          backupPolicyConfig.scheduleMode ===
            ScheduleMode.SCHEDULE_MODE_UNSPECIFIED
            ? ScheduleMode.SCHEDULE_MODE_BASIC
            : backupPolicyConfig.scheduleMode,
        policyType:
          backupPolicyConfig.policyType ||
          BackupPolicyType.BACKUP_POLICY_TYPE_STANDARD,
        resourceTypes: backupPolicyConfig.resourceTypes,
      }}
      title={
        backupPolicyConfig.id
          ? 'Edit backup policy'
          : creatingNew
            ? 'Create backup policy'
            : 'Duplicate backup policy'
      }
      stepperLabels={[
        'Resource selection',
        'Resource list',
        'Backup schedules',
      ]}
    />
  );
}
